// hooks/usePendingRequests.ts
// Counts pending agent_requests + shop_requests for the sidebar badge.
// Updates in real-time via Supabase postgres_changes (both tables now in the
// supabase_realtime publication — see migration enable_realtime_requests_handovers).

import { useState, useEffect, useRef, useCallback } from "react";
import { supabase } from "../lib/supabase";

export function usePendingRequests(ownerId: string | undefined) {
  const [pendingCount, setPendingCount] = useState(0);
  const ownerRef = useRef(ownerId);

  // Keep ref in sync so the realtime callback always has the latest value
  useEffect(() => { ownerRef.current = ownerId; }, [ownerId]);

  const refetch = useCallback(async (uid: string) => {
    const [{ count: agentCount }, { count: shopCount }] = await Promise.all([
      supabase
        .from("agent_requests")
        .select("id", { count: "exact", head: true })
        .eq("owner_id", uid)
        .eq("status", "pending"),
      supabase
        .from("shop_requests")
        .select("id", { count: "exact", head: true })
        .eq("owner_id", uid)
        .eq("status", "pending"),
    ]);
    setPendingCount((agentCount ?? 0) + (shopCount ?? 0));
  }, []);

  // Initial fetch
  useEffect(() => {
    if (!ownerId) return;
    refetch(ownerId);
  }, [ownerId, refetch]);

  // Realtime — subscribe once, re-fetch on any change to either table
  useEffect(() => {
    const ch = supabase
      .channel("pending-requests-badge-v2")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "agent_requests" },
        (payload) => {
          const uid = ownerRef.current;
          if (!uid) return;
          const record = (payload.new ?? payload.old) as any;
          if (record?.owner_id !== uid) return;
          refetch(uid);
        },
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "shop_requests" },
        (payload) => {
          const uid = ownerRef.current;
          if (!uid) return;
          const record = (payload.new ?? payload.old) as any;
          if (record?.owner_id !== uid) return;
          refetch(uid);
        },
      )
      .subscribe();

    return () => { supabase.removeChannel(ch); };
  }, [refetch]); // subscribe once for lifetime of app

  return pendingCount;
}
